import { IApiMethods } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import * as React from "react";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaNameModal, KuikaNameModalFormValues } from "../../ui-kit/kuika-name-modal/kuika-name-modal";

/**
 * Create or rename one API method, and the ONE place the method-name rule lives.
 *
 * It is the shared create-modal skeleton with a second field rather than a fourth
 * hand-built sheet: a method carries a description as well as a name. Everything else it
 * has — HTTP verb, content types, authorization, actions and inputs — is edited in the
 * right panel once the method exists.
 */

/** Start with a letter, then letters, digits or spaces — it becomes the generated function name. */
export const METHOD_NAME_PATTERN: RegExp = /^[A-Za-z][A-Za-z0-9 ]+$/;
export const METHOD_NAME_MESSAGE: string = "Please enter a valid Method Name.";

export interface IApiMethodModalProps {
  visible: boolean;
  /** The method being renamed, or undefined when creating. */
  editingMethod?: IApiMethods;
  /** Every method on the open API — the uniqueness check runs against all of them. */
  methods: IApiMethods[];
  /**
   * Where the save stands. The ✓ is held on Save before the sheet closes onto the methods table,
   * and a failed save leaves it open on "Try again".
   */
  status: KuikaButtonStatus;
  onCancel: () => void;
  onSubmit: (name: string, description?: string) => void;
}

export class ApiMethodModal extends React.PureComponent<IApiMethodModalProps> {
  /**
   * Uniqueness as an inline rule rather than the warning toast it used to be: the message
   * lands on the field that caused it, and a rename excludes the method itself so a
   * description-only edit is not rejected by its own name.
   */
  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return;
    const clash: boolean = (this.props.methods ?? []).some(
      (method: IApiMethods): boolean =>
        method.name?.toLowerCase() === value.toLowerCase() && method.id !== this.props.editingMethod?.id
    );
    if (clash) throw new Error("Method name already exist");
  };

  handleSubmit = (name: string, values: KuikaNameModalFormValues): void => {
    this.props.onSubmit(name, values.methodDescription);
  };

  render() {
    const isEditing: boolean = !!this.props.editingMethod;

    return (
      <KuikaNameModal
        visible={this.props.visible}
        title={isEditing ? "Edit Method" : "New Method"}
        subtitle={
          isEditing
            ? "Rename this method or update what it is for."
            : "Name the operation this API exposes — its actions and inputs come next."
        }
        iconName="code"
        confirmLabel="Save"
        status={this.props.status}
        fieldName="methodName"
        placeholder="Enter method name"
        initialValue={this.props.editingMethod?.name}
        rules={[
          { required: true, message: "Please enter a Method Name." },
          { pattern: METHOD_NAME_PATTERN, message: METHOD_NAME_MESSAGE },
          { validator: this.existingNameValidator }
        ]}
        // The ids the method form has always emitted, so the E2E suite still finds them.
        inputTestId="methodNameInput"
        confirmTestId="saveButton"
        className="kuika-api-method-modal"
        extraFields={
          <Form.Item name="methodDescription" label="Description" initialValue={this.props.editingMethod?.description}>
            <KuikaInput
              data-testid="methodDescriptionInput"
              size="md"
              type="textarea"
              rows={3}
              placeholder="What does this method do?"
            />
          </Form.Item>
        }
        onCancel={this.props.onCancel}
        onSubmit={this.handleSubmit}
      />
    );
  }
}
